import React, { useState } from "react";
import Card from "../../shared/Card";
import { getScoreGradient } from "../../../utils/colorUtils";
import {
  HealthScore,
  RiskExposureChart,
  generateRiskScore,
} from "./HealthAndRiskMetricsDetails";
import { useTheme } from '../../../contexts/ThemeContext';

const DetailModal = ({ isOpen, onClose, score }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onClose}
      ></div>
      <div
        className={`relative bg-white dark:bg-gray-900 rounded-lg shadow-xl dark:shadow-2xl dark:shadow-black/20 w-full max-w-4xl transform transition-all duration-500 
        ${isOpen ? "scale-100 rotate-0 opacity-100" : "scale-0 rotate-180 opacity-0"}`}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Financial Health Score Details
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Score Breakdown */}
            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Score Components
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1 text-gray-900 dark:text-gray-100">
                      <span>Emergency Fund</span>
                      <span className="font-medium">95%</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500"
                        style={{ width: "95%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1 text-gray-900 dark:text-gray-100">
                      <span>Debt Management</span>
                      <span className="font-medium">82%</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: "82%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1 text-gray-900 dark:text-gray-100">
                      <span>Savings Rate</span>
                      <span className="font-medium">78%</span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-lime-500"
                        style={{ width: "78%" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Historical Trend
                </h4>
                <div className="aspect-w-16 aspect-h-9">
                  {/* Placeholder for trend chart */}
                  <div className="w-full h-full bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg flex items-center justify-center text-gray-900 dark:text-gray-100">
                    Health Score History Chart
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Analysis */}
            <div className="space-y-6">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Key Metrics
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="text-sm text-gray-900 dark:text-gray-100">Debt-to-Income</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      28%
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">
                      Below 30% threshold
                    </div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="text-sm text-gray-900 dark:text-gray-100">Savings Rate</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      15%
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">Above target</div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="text-sm text-gray-900 dark:text-gray-100">Emergency Fund</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      6 mo
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">Exceeds goal</div>
                  </div>
                  <div className="bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="text-sm text-gray-900 dark:text-gray-100">Credit Score</div>
                    <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      780
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">Excellent</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Recommendations
                </h4>
                <ul className="space-y-2 text-sm text-gray-900 dark:text-gray-100">
                  <li className="flex items-start">
                    <span className="w-2 h-2 mt-1.5 rounded-full bg-green-500 mr-2"></span>
                    <span>Consider increasing retirement contributions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 mt-1.5 rounded-full bg-yellow-500 mr-2"></span>
                    <span>Review insurance coverage for potential gaps</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 mr-2"></span>
                    <span>Explore tax-advantaged investment options</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FinancialHealthCard = ({ healthScore }) => {
  const { theme } = useTheme();
  const [showDetail, setShowDetail] = useState(false);
  const gradientColors = getScoreGradient(healthScore);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getProgressColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const handleCardClick = () => {
    setShowDetail(true);
  };

  return (
    <>
      <Card
        className="relative overflow-hidden pb-0 hover:scale-[1.03] hover:shadow-xl active:scale-95 transition-all duration-200 cursor-pointer group"
        onClick={handleCardClick}
      >
        {/* Background Gradient */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${gradientColors.background} opacity-50 dark:opacity-0`}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br dark:from-gray-800 dark:to-gray-800/95"></div>

        <div className="relative h-full flex flex-col">
          <div className="flex justify-between items-start">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center mt-0">
              <svg
                className={`w-5 h-5 ${gradientColors.text} mr-2`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Financial Health Score
            </h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDetail(true);
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
              title="View Details"
            >
              <svg
                className="w-5 h-5 text-gray-500 dark:text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </button>
          </div>

          <div className="transform transition-transform duration-200 hover:scale-105 my-6">
            <HealthScore score={healthScore} />
          </div>

          <div className="mt-4 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Health Indicators
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <li className="flex items-center">
                <span
                  className={`w-2 h-2 rounded-full mr-2 ${gradientColors.progress}`}
                ></span>
                Strong Emergency Fund
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Optimal Debt Ratio
              </li>
              <li className="flex items-center">
                <span className="w-2 h-2 bg-teal-500 rounded-full mr-2"></span>
                Regular Savings Rate
              </li>
            </ul>
          </div>
        </div>

        {/* Progress Bar at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gray-200 dark:bg-gray-700">
          <div
            className={`h-full ${gradientColors.progress} transition-all duration-500 ease-out`}
            style={{ width: `${healthScore}%` }}
          ></div>
        </div>
      </Card>

      <DetailModal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        score={healthScore}
      />
    </>
  );
};

export default FinancialHealthCard;
